import React, { ReactElement, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Box, Container, Theme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import Logo from '@components/core/media/Logo'
import NavigationItem from './components/NavigationItem'
import MenuToggle from '@components/core/input/MenuToggle'
import { Link as GatsbyLink } from '@gatsbyjs/reach-router'
import Icon from '@components/core/media/Icon'
import Link from '@components/core/input/Link'

export type NavigationProps = {
  itemHome: MCDC.Props.ILinkTo
  itemAccount: MCDC.Props.ILinkTo
  items: MCDC.Props.ILinkTo[]
  activeIndex: number
  isOpen: boolean
  onToggle: () => void
  sx?: MCDC.Props.IDefault['sx']
}

export default function Navigation({
  items,
  itemHome,
  itemAccount,
  activeIndex,
  isOpen,
  onToggle,
  sx,
}: NavigationProps): ReactElement {
  const [isFavoriteHovered, setIsFavoriteHovered] = React.useState(false)
  const intl = useIntl()
  const navigationBypass = useRef<HTMLAnchorElement>(null)

  // Add skip link focus handler on mount, and clean up on unmount
  useEffect(() => {
    // Handler to focus the main content area when skip link is activated
    const handler = () => {
      if (document.getElementById('main')) {
        document.getElementById('main')?.focus()
      }
    }
    // Attach click event to the skip link ("Skip to main content")
    if (navigationBypass.current) {
      navigationBypass.current.addEventListener('click', handler)
    }
    // Clean up event listener on unmount to prevent memory leaks
    return () => {
      if (navigationBypass.current) {
        navigationBypass.current.removeEventListener('click', handler)
      }
    }
  }, [])

  return (
    <Container
      maxWidth="xl"
      sx={[
        (theme: Theme) => ({
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          height: '64px',
          backgroundColor: theme.palette.grey[100],
          zIndex: theme.zIndex.drawer + 1,
          px: `${theme.spacing(theme.gutter('sm'))} !important`,
        }),
        sx,
      ]}
    >
      <Link to="#main" sx={visuallyHidden} ref={navigationBypass}>
        {intl.formatMessage({
          id: 'label.skipToMainContent',
        })}
      </Link>

      <GatsbyLink
        to={itemHome.url}
        title={itemHome.label as string}
        aria-label={itemHome.label as string}
      >
        <Logo sx={{ mr: 4 }} />
      </GatsbyLink>
      <Box
        component="ul"
        sx={(theme: Theme) => ({
          mx: 'auto',
          whiteSpace: 'nowrap',
          p: 0,
          '> *': {
            mr: 6,
            '&:last-child': { mr: 0 },
          },
          [theme.breakpoints.down('lg')]: {
            display: 'none',
          },
        })}
      >
        {items.map((entry, index) => (
          <NavigationItem
            to={entry.url}
            isExternal={entry.isExternal}
            count={entry.count}
            isSelected={activeIndex === index}
            key={index}
          >
            {entry.label}
          </NavigationItem>
        ))}
      </Box>
      <Box
        sx={(theme: Theme) => ({
          ml: 'auto',
          mr: -2,
          [theme.breakpoints.up('lg')]: {
            height: '100%',
            ml: 0,
            mr: -4,
          },
        })}
      >
        <MenuToggle
          onClick={onToggle}
          isToggled={isOpen}
          sx={(theme: Theme) => ({
            [theme.breakpoints.up('lg')]: {
              display: 'none',
            },
          })}
        />
        <Link
          to={itemAccount.url}
          underline="none"
          sx={(theme: Theme) => ({
            display: 'inline-flex',
            width: '53px',
            height: '100%',
            justifyContent: 'center',
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
          })}
          ariaLabel={itemAccount.label as string}
          title={itemAccount.label as string}
          onMouseOver={() => setIsFavoriteHovered(true)}
          onMouseOut={() => setIsFavoriteHovered(false)}
        >
          <Icon
            variant={isFavoriteHovered ? 'favorite_filled' : 'favorite'}
            sx={{ color: 'tertiary.main' }}
          />
        </Link>
      </Box>
    </Container>
  )
}
